// web/src/components/MatchPhoto.tsx
// Photo gallery for a single match — supports multiple photos (plan-capped:
// free = 1, paid = 5). Lays out a responsive grid of thumbnails with an
// "Add photo" tile until the cap is hit. Each thumbnail has a remove button
// gated behind a confirmation step.
//
// Upload flow (per photo):
//   1. POST /photo/presign → { uploadUrl, fields, key }
//   2. Browser POSTs FormData to S3 directly
//   3. POST /photo/confirm with the key → server appends to imagePaths
//   4. Caller's onChange refreshes the parent match data
//
// We refetch the signed URL list from the server after each mutation rather
// than splicing it client-side — keeps the source of truth on the server and
// avoids stale URLs.

import { useEffect, useRef, useState } from 'react';
import { useMatchApi } from '../api/match';
import { ApiError } from '../api/client';
import { compressForUpload } from '../lib/image-resize';
import { PlanCapReached } from './PlanCapReached';
import type { PlanLimits } from '../types';

const ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
const MAX_BYTES = 10 * 1024 * 1024;

interface Photo {
  key: string;
  suffix: string;
  url: string;
}

interface Props {
  kidId: string;
  matchId: string;
  /** True when the match has any photo (legacy imagePath or any in imagePaths[]). */
  hasPhoto: boolean;
  /** Plan-resolved per-match cap. Used to gate the "Add photo" tile. */
  limit?: number;
  /** Optional — used to surface "X of Y" to the user. */
  plan?: PlanLimits['plan'];
  /** Refetch the match after a successful upload or delete. */
  onChange: () => Promise<void> | void;
}

export function MatchPhoto({ kidId, matchId, hasPhoto, limit = 1, plan, onChange }: Props) {
  const api = useMatchApi();
  const fileRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // F1: photo cap-hit → full-screen PlanCapReached, not a tiny error.
  const [capHit, setCapHit] = useState(false);

  const reload = async () => {
    if (!hasPhoto) {
      setPhotos([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.listPhotos(kidId, matchId);
      setPhotos(res.photos);
    } catch {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial + when match/kid/hasPhoto changes — keeps URLs fresh after nav.
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kidId, matchId, hasPhoto]);

  const choose = () => fileRef.current?.click();

  const upload = async (rawFile: File) => {
    setError(null);
    if (!ALLOWED.has(rawFile.type)) {
      setError('Image must be JPEG, PNG, WebP, or HEIC.');
      return;
    }
    if (rawFile.size > MAX_BYTES) {
      setError(`Image must be under ${Math.round(MAX_BYTES / 1024 / 1024)}MB.`);
      return;
    }
    setUploading(true);
    try {
      // Compress in the browser: resize → 2048px longest edge → WebP @ q0.82.
      // Cuts a 5MB iPhone JPEG to ~280KB; saves us S3 + CloudFront egress.
      // No-op for already-small files. Falls back to original if it fails.
      const file = await compressForUpload(rawFile);
      const presign = await api.presignPhoto(kidId, matchId, file.type, file.size);
      const formData = new FormData();
      for (const [k, v] of Object.entries(presign.fields)) {
        formData.append(k, v);
      }
      formData.append('file', file);
      const putRes = await fetch(presign.uploadUrl, { method: 'POST', body: formData });
      if (!putRes.ok) throw new Error(`Upload failed (HTTP ${putRes.status})`);
      // Confirm with the server — this is where the per-plan cap is enforced
      // race-safely (two parallel uploads can't both win past the cap).
      await api.confirmPhoto(kidId, matchId, presign.key);
      await onChange();
      await reload();
    } catch (err) {
      const e = err as ApiError;
      if (e.status === 403 && e.code === 'plan_limit_reached') {
        setCapHit(true);
      } else if (e.status === 403) {
        setError(e.message || `Per-match photo limit reached (${limit}). Delete one to add another.`);
      } else {
        setError(e.message || 'Upload failed. Try again.');
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (suffix: string) => {
    setRemoving(suffix);
    setError(null);
    try {
      await api.removePhotoBySuffix(kidId, matchId, suffix);
      await onChange();
      await reload();
    } catch (err) {
      setError((err as Error).message || 'Could not remove.');
    } finally {
      setRemoving(null);
      setConfirmingRemove(null);
    }
  };

  const canAddMore = photos.length < limit;
  const helpCopy =
    plan === 'free'
      ? `Free plan: ${limit} photo per match. JPEG/PNG/WebP/HEIC up to 10MB.`
      : `Up to ${limit} photos per match. JPEG/PNG/WebP/HEIC up to 10MB.`;

  return (
    <div style={{ marginBottom: 24 }}>
      {capHit ? (
        <PlanCapReached
          cap="photos"
          currentCount={photos.length}
          onDismiss={() => setCapHit(false)}
        />
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h2 style={{ fontSize: 18, margin: 0 }}>Photos</h2>
        <span style={{ fontSize: 'var(--small)', color: 'var(--muted)' }}>
          {photos.length} / {limit}
        </span>
      </div>

      {loading && photos.length === 0 ? (
        <p style={{ color: 'var(--muted)', fontSize: 'var(--small)' }}>Loading…</p>
      ) : null}

      {/* Gallery grid: responsive tiles. Min 140 / max 1fr keeps everything
          legible at any width without needing a JS breakpoint hack. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 12,
        }}
      >
        {photos.map((p) => (
          <div
            key={p.key}
            style={{
              position: 'relative',
              borderRadius: 12,
              overflow: 'hidden',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              aspectRatio: '4 / 3',
            }}
          >
            <img
              src={p.url}
              alt="Match photo"
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            {confirmingRemove === p.suffix ? (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(20, 32, 24, 0.78)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: 8,
                }}
              >
                <p style={{ color: 'var(--ink-on-green)', fontSize: 'var(--small)', margin: 0, textAlign: 'center' }}>
                  Remove this photo?
                </p>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    disabled={removing === p.suffix}
                    onClick={() => void remove(p.suffix)}
                    style={{
                      background: 'var(--error)',
                      color: 'var(--ink-on-green)',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: 999,
                      cursor: 'pointer',
                      fontSize: 'var(--small)',
                      fontFamily: 'inherit',
                    }}
                  >
                    {removing === p.suffix ? '…' : 'Remove'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingRemove(null)}
                    style={{
                      background: 'transparent',
                      color: 'var(--ink-on-green)',
                      border: '1px solid rgba(250,245,236,0.5)',
                      padding: '6px 12px',
                      borderRadius: 999,
                      cursor: 'pointer',
                      fontSize: 'var(--small)',
                      fontFamily: 'inherit',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingRemove(p.suffix)}
                aria-label="Remove photo"
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  border: 'none',
                  background: 'rgba(20,32,24,0.55)',
                  color: 'var(--ink-on-green)',
                  cursor: 'pointer',
                  fontSize: 14,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                ✕
              </button>
            )}
          </div>
        ))}

        {/* Add-photo tile — only shown while under the cap. */}
        {canAddMore ? (
          <button
            type="button"
            onClick={choose}
            disabled={uploading}
            style={{
              border: '1px dashed var(--border)',
              borderRadius: 12,
              background: 'var(--surface)',
              color: 'var(--muted)',
              cursor: uploading ? 'progress' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: 12,
              fontFamily: 'inherit',
              fontSize: 'var(--small)',
              aspectRatio: '4 / 3',
            }}
          >
            <span style={{ fontSize: 24, lineHeight: 1 }}>+</span>
            <span>{uploading ? 'Uploading…' : photos.length === 0 ? 'Add a photo' : 'Add another'}</span>
          </button>
        ) : null}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
        }}
      />

      <p style={{ marginTop: 12, color: 'var(--muted)', fontSize: 'var(--small)' }}>
        {helpCopy}
      </p>

      {error ? <p style={{ color: 'var(--error)', fontSize: 14, marginTop: 8 }}>{error}</p> : null}
    </div>
  );
}
